import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faIdCard, faEnvelope, faPhone, faLock, faSitemap } from '@fortawesome/free-solid-svg-icons';
import { getAccessToken, getAuth, isSecretaryContextUser } from '../authStorage';
import { fetchDepartments } from '../departmentsApi';
import {
  createSecretaryUser,
  fetchSecretaryUsers,
  updateSecretaryUser,
} from '../secretaryUsersApi';
import { useLanguage } from '../i18n/language';
import { useT } from '../i18n/translations';
import { getSampleSecretaryUserForm } from '../secretaryFormUtils';
import './BranchesPage.css';
import './DecisionsPage.css';
import './UsersPage.css';
import './SecretaryUserFormPage.css';

const emptyForm = () => ({
  userName: '',
  fullName: '',
  email: '',
  phone: '',
  password: '',
  departmentId: '',
  isActive: true,
});

const defaultOfficerPermissions = {
  dashboard: { pageAccess: true, actions: {}, ui: {} },
  operational_plans: { pageAccess: true, actions: {}, ui: {} },
  departments: { pageAccess: true, actions: {}, ui: {} },
  branches: { pageAccess: true, actions: {}, ui: {} },
  board: { pageAccess: true, actions: {}, ui: {} },
  committees: { pageAccess: true, actions: {}, ui: {} },
  shareholders: { pageAccess: true, actions: {}, ui: {} },
  decisions: { pageAccess: true, actions: {}, ui: {} },
  decision_types: { pageAccess: true, actions: {}, ui: {} },
  meetings: { pageAccess: true, actions: {}, ui: {} },
  nominations: { pageAccess: true, actions: {}, ui: {} },
  permissions: { pageAccess: true, actions: {}, ui: {} },
};

/**
 * Add / edit secretary (Officer) — full page, not modal.
 * Routes: /app/secretaries/new, /app/secretaries/:id/edit
 */
export default function SecretaryUserFormPage({ mode }) {
  const { language, direction } = useLanguage();
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.startsWith('/officer') ? '/officer' : '/app';
  const { id: idParam } = useParams();
  const isNew = mode === 'new';
  const editId = isNew ? null : idParam;
  const invalidEditId = !isNew && (!editId || !String(editId).trim());

  const [accessDenied, setAccessDenied] = useState(false);
  const [loading, setLoading] = useState(!isNew);
  const [loadError, setLoadError] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [departmentOptions, setDepartmentOptions] = useState([]);

  const companyId = getAuth()?.user?.companyId;
  const sortLocale = (language === 'ar' || language === 'ur') ? 'ar' : 'en';

  useEffect(() => {
    let cancelled = false;
    if (!getAccessToken() || !companyId) {
      setDepartmentOptions([]);
      return undefined;
    }
    (async () => {
      try {
        const rows = await fetchDepartments();
        if (cancelled) return;
        const cid = String(companyId);
        const scoped = rows
          .filter((d) => String(d.branchId) === cid)
          .sort((a, b) => (a.name || '').localeCompare(b.name || '', sortLocale));
        setDepartmentOptions(scoped);
      } catch {
        if (!cancelled) setDepartmentOptions([]);
      }
    })();
    return () => { cancelled = true; };
  }, [companyId, sortLocale]);

  const goList = useCallback(() => navigate(`${basePath}/secretaries`), [navigate, basePath]);

  const loadForEdit = useCallback(async () => {
    if (!editId) return;
    setLoading(true);
    setLoadError('');
    try {
      const users = await fetchSecretaryUsers();
      const u = users.find((x) => String(x.id) === String(editId));
      if (!u) {
        setLoadError(t('common.noResults'));
        return;
      }
      setForm({
        userName: u.userName,
        fullName: u.fullName || '',
        email: u.email || '',
        phone: u.phone || '',
        password: '',
        departmentId: u.departmentId ? String(u.departmentId) : '',
        isActive: !!u.isActive,
      });
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : t('users.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [editId, t]);

  useEffect(() => {
    if (!getAccessToken()) {
      navigate('/login', { replace: true });
      return;
    }
    if (!isSecretaryContextUser()) {
      setAccessDenied(true);
      setLoading(false);
      return;
    }
    if (invalidEditId) {
      setLoadError(t('common.noResults'));
      setLoading(false);
      return;
    }
    if (isNew) {
      setForm(emptyForm());
      setLoading(false);
      return;
    }
    loadForEdit();
    // Run on mount / when the target record changes — NOT every render.
    // `t` and `loadForEdit` are excluded because `useT()` returns a new
    // function each render, which caused an infinite setState loop.
  }, [navigate, isNew, invalidEditId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFillSample = useCallback(() => {
    if (!isNew) return;
    setError('');
    setForm(getSampleSecretaryUserForm());
  }, [isNew]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.userName.trim()) {
      setError(t('users.valName'));
      return;
    }
    if (isNew && !form.password.trim()) {
      setError(t('users.valPassword'));
      return;
    }
    setSubmitting(true);
    setError('');
    const deptPayload = () => {
      const raw = String(form.departmentId || '').trim();
      const n = Number(raw);
      return Number.isFinite(n) && n > 0 ? n : null;
    };

    try {
      if (isNew) {
        await createSecretaryUser({
          userName: form.userName.trim(),
          password: form.password,
          fullName: form.fullName.trim() || undefined,
          email: form.email.trim() || undefined,
          phone: form.phone.trim() || undefined,
          departmentId: deptPayload(),
          permissionsJson: JSON.stringify(defaultOfficerPermissions),
        });
      } else {
        await updateSecretaryUser(editId, {
          userName: form.userName.trim(),
          fullName: form.fullName.trim() || undefined,
          email: form.email.trim() || undefined,
          phone: form.phone.trim() || undefined,
          departmentId: deptPayload(),
          newPassword: form.password.trim() || undefined,
          isActive: form.isActive,
        });
      }
      goList();
    } catch (err) {
      setError(err instanceof Error ? err.message : (isNew ? t('users.saveFailed') : t('users.saveFailed')));
    } finally {
      setSubmitting(false);
    }
  };

  if (accessDenied) {
    return (
      <div className="page" dir={direction}>
        <p style={{ padding: 24, color: '#78909c', fontFamily: 'Cairo, sans-serif' }}>{t('common.noPerm')}</p>
      </div>
    );
  }

  const title = isNew ? t('users.titleNew') : t('users.titleEdit');
  return (
    <div className="secretary-v2" dir={direction}>
      <div className="page-wrapper">
        {loadError && <div className="v2-error">{loadError}</div>}
        {loading && <div className="v2-loading">{t('common.loading')}</div>}
        {!loading && !loadError && (
          <div className="card">
            <div className="card-top">
              <div className="top-bar top-bar--single-line">
                <div className="page-title-row">
                  <h1 className="page-title">
                    <span className="page-title-icon"><FontAwesomeIcon icon={faUser} /></span>
                    {title}
                  </h1>
                </div>
                <div className="action-btns">
                  <button type="submit" form="secretary-user-form" className="btn btn-primary" disabled={submitting || loading || !!loadError}>
                    {submitting ? t('users.saving') : isNew ? t('users.add') : t('users.saveBtn')}
                  </button>
                  <button type="button" className="btn btn-ghost" onClick={goList}>{t('common.cancel')}</button>
                  {isNew && (
                    <button type="button" className="btn btn-outline" onClick={handleFillSample} disabled={submitting || loading}>
                      {t('branches.fillSample')}
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="card-inner">
              <form id="secretary-user-form" onSubmit={handleSubmit}>
                {error && <div className="v2-error">{error}</div>}
                <div className="fields-grid">
                  <div className="field-group">
                    <label className="field-label"><span className="label-icon"><FontAwesomeIcon icon={faUser} /></span>{t('users.username')} <span className="required">*</span></label>
                    <div className="field-input">
                      <input placeholder={t('users.username')} value={form.userName} onChange={(e) => setForm((p) => ({ ...p, userName: e.target.value }))} autoComplete="username" />
                    </div>
                  </div>
                  <div className="field-group">
                    <label className="field-label"><span className="label-icon"><FontAwesomeIcon icon={faIdCard} /></span>{t('users.lblFullName')}</label>
                    <div className="field-input">
                      <input placeholder={t('users.lblFullName')} value={form.fullName} onChange={(e) => setForm((p) => ({ ...p, fullName: e.target.value }))} />
                    </div>
                  </div>
                  <div className="field-group">
                    <label className="field-label"><span className="label-icon"><FontAwesomeIcon icon={faEnvelope} /></span>{t('common.email')}</label>
                    <div className="field-input">
                      <input type="email" dir="ltr" placeholder="example@company.com" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} />
                    </div>
                  </div>
                  <div className="field-group">
                    <label className="field-label"><span className="label-icon"><FontAwesomeIcon icon={faPhone} /></span>{t('common.phone')}</label>
                    <div className="field-input">
                      <input dir="ltr" placeholder="05xxxxxxxx" value={form.phone} onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))} />
                    </div>
                  </div>
                  <div className="field-group span-2">
                    <label className="field-label"><span className="label-icon"><FontAwesomeIcon icon={faSitemap} /></span>{t('users.lblDept')}</label>
                    <div className="field-input select-wrapper">
                      <select
                        value={form.departmentId}
                        onChange={(e) => setForm((p) => ({ ...p, departmentId: e.target.value }))}
                        disabled={!companyId}
                      >
                        <option value="">—</option>
                        {departmentOptions.map((d) => (
                          <option key={d.id} value={String(d.id)}>
                            {d.parentName ? `${d.parentName} › ${d.name}` : d.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
                <div className="section-divider" />
                <div className="fields-grid">
                  <div className="field-group span-2">
                    <label className="field-label"><span className="label-icon"><FontAwesomeIcon icon={faLock} /></span>{isNew ? t('auth.password') : t('auth.password')}{isNew && <span className="required">*</span>}</label>
                    <div className="field-input">
                      <input type="password" dir="ltr" placeholder={isNew ? t('auth.password') : t('board.wizLeaveBlank')} autoComplete="new-password" value={form.password} onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))} />
                    </div>
                  </div>
                </div>
                {!isNew && (
                  <div style={{ marginTop: 18 }}>
                    <label className="field-label" style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                      <input type="checkbox" checked={form.isActive} onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))} />
                      {t('common.active')}
                    </label>
                  </div>
                )}
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
